import { API_CONFIG, ApiResponse, PaginatedResponse, ApiError, getAuthHeaders, buildApiUrl } from '../config/api';

// Generic API Request Function
const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {},
  params?: Record<string, string>
): Promise<ApiResponse<T>> => {
  const url = buildApiUrl(endpoint, params);
  
  const config: RequestInit = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP Error: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return {
      success: true,
      data,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw new ApiError(
      error instanceof Error ? error.message : 'Network Error',
      0,
      error
    );
  }
};

// Authenticated API Request Function
export const authenticatedRequest = async <T = any>(
  endpoint: string,
  token: string,
  options: RequestInit = {},
  params?: Record<string, string>
): Promise<ApiResponse<T>> => {
  const url = buildApiUrl(endpoint, params);
  
  const config: RequestInit = {
    ...options,
    headers: {
      ...getAuthHeaders(token),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid
        throw new ApiError('Unauthorized - Token expired', 401);
      }
      
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP Error: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return {
      success: true,
      data,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    throw new ApiError(
      error instanceof Error ? error.message : 'Network Error',
      0,
      error
    );
  }
};

/** Admin panel girişi — [User].IsAdmin = 1 ve şifre SHA256 ile eşleşmeli */
export const adminAuthService = {
  login: async (email: string, password: string): Promise<ApiResponse<{ success?: boolean; token: string; user: { id: string; email: string; name: string; isAdmin: boolean } }>> => {
    return apiRequest(API_CONFIG.ENDPOINTS.ADMIN_LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
};

// Authentication Service (DailyPositive API — mobil)
export const authService = {
  login: async (email: string, password: string): Promise<ApiResponse<{ token: string; user: any }>> => {
    return apiRequest(API_CONFIG.ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  register: async (userData: any): Promise<ApiResponse<{ token: string; user: any }>> => {
    return apiRequest(API_CONFIG.ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  getProfile: async (token: string): Promise<ApiResponse<any>> => {
    return authenticatedRequest(API_CONFIG.ENDPOINTS.PROFILE, token, {
      method: 'GET',
    });
  },
};

// Support System Service
export const supportService = {
  getTickets: async (
    token: string,
    params?: {
      status?: string;
      priority?: string;
      page?: number;
      pageSize?: number;
    }
  ): Promise<ApiResponse<PaginatedResponse<any>>> => {
    const queryParams: Record<string, string> = {};
    if (params) {
      if (params.status && params.status !== 'all') queryParams.status = params.status;
      if (params.priority && params.priority !== 'all') queryParams.priority = params.priority;
      if (params.page) queryParams.page = params.page.toString();
      if (params.pageSize) queryParams.pageSize = params.pageSize.toString();
    }
    
    return authenticatedRequest(API_CONFIG.ENDPOINTS.SUPPORT_TICKETS, token, {
      method: 'GET',
    }, queryParams);
  },

  getCategories: async (): Promise<ApiResponse<any[]>> => {
    return apiRequest(API_CONFIG.ENDPOINTS.SUPPORT_CATEGORY_LIST, {
      method: 'GET',
    });
  },

  getStatistics: async (token: string): Promise<ApiResponse<any>> => {
    return authenticatedRequest(API_CONFIG.ENDPOINTS.SUPPORT_STATISTICS, token, {
      method: 'GET',
    });
  },

  sendMessage: async (
    token: string,
    ticketId: string,
    message: string,
    isInternal: boolean = false
  ): Promise<ApiResponse> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.SUPPORT_MESSAGES}/${ticketId}/messages`, token, {
      method: 'POST',
      body: JSON.stringify({
        body: message,
        isInternal,
      }),
    });
  },

  updateTicketStatus: async (
    token: string,
    ticketId: string,
    status: number
  ): Promise<ApiResponse> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.SUPPORT_MESSAGES}/${ticketId}`, token, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  getTicketDetails: async (
    token: string,
    ticketId: string
  ): Promise<ApiResponse<any>> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.SUPPORT_MESSAGES}/${ticketId}`, token, {
      method: 'GET',
    });
  },
};

export interface SentenceInfoRow {
  id: number;
  sHead: string | null;
  sDetail: string | null;
  sDate: string | null;
  /** Sunucu NVARCHAR ise metin; eski kayıtlar sayısal da olabilir */
  sState: string | number | null;
}

export const sentenceInfoService = {
  list: async (token: string, take = 100): Promise<ApiResponse<{ success: boolean; items: SentenceInfoRow[] }>> => {
    return authenticatedRequest(API_CONFIG.ENDPOINTS.SENTENCE_INFO, token, { method: 'GET' }, { take: take.toString() });
  },

  create: async (
    token: string,
    body: { sHead?: string; sDetail?: string; sDate?: string }
  ): Promise<ApiResponse<{ success: boolean; id: number }>> => {
    return authenticatedRequest(API_CONFIG.ENDPOINTS.SENTENCE_INFO, token, {
      method: 'POST',
      body: JSON.stringify({
        sHead: body.sHead,
        sDetail: body.sDetail,
        sDate: body.sDate ?? undefined,
      }),
    });
  },

  delete: async (token: string, id: number): Promise<ApiResponse<{ success: boolean }>> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.SENTENCE_INFO}/${id}`, token, {
      method: 'DELETE',
    });
  },
};

export interface AffirmationPlanListItem {
  id: string;
  userName: string;
  userEmail: string;
  topic: string;
  goal: string;
  packageName?: string;
  planTitle?: string;
  requestedAt: string;
  status: string;
  startDate?: string;
  endDate?: string;
  currentDay: number;
  completionPercentage: number;
  adminName?: string;
  updatedAt: string;
  isTrial: boolean;
}

export interface AffirmationPlanAnalytics {
  totalRequests: number;
  pendingApproval: number;
  activePlans: number;
  completedPlans: number;
  rejectedPlans: number;
  generationFailed: number;
}

export const affirmationPlanAdminService = {
  list: async (
    token: string,
    params?: { status?: string; page?: number; pageSize?: number }
  ): Promise<ApiResponse<AffirmationPlanListItem[]>> => {
    const query: Record<string, string> = {};
    if (params?.status) query.status = params.status;
    if (params?.page) query.page = String(params.page);
    if (params?.pageSize) query.pageSize = String(params.pageSize);
    return authenticatedRequest(API_CONFIG.ENDPOINTS.AFFIRMATION_PLANS, token, { method: 'GET' }, query);
  },

  analytics: async (token: string): Promise<ApiResponse<AffirmationPlanAnalytics>> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.AFFIRMATION_PLANS}/analytics`, token, {
      method: 'GET',
    });
  },

  get: async (token: string, planId: string): Promise<ApiResponse<any>> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.AFFIRMATION_PLANS}/${planId}`, token, {
      method: 'GET',
    });
  },

  post: async (token: string, planId: string, path: string, body?: unknown): Promise<ApiResponse> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.AFFIRMATION_PLANS}/${planId}${path}`, token, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  updateDay: async (
    token: string,
    planId: string,
    dayNumber: number,
    body: unknown
  ): Promise<ApiResponse> => {
    return authenticatedRequest(
      `${API_CONFIG.ENDPOINTS.AFFIRMATION_PLANS}/${planId}/days/${dayNumber}`,
      token,
      { method: 'PUT', body: JSON.stringify(body) }
    );
  },
};

// ---------------------------------------------------------------------------
// iOS Winback kampanyası — abone olmayanları görüntüleme ve birebir bildirim
// Backend: DailyPositiveServer/Controllers/AdminWinbackController.cs
// ---------------------------------------------------------------------------

export interface WinbackSettings {
  enabled: boolean;
  testMode: boolean;
  realSendEnabled: boolean;
  platform: string;
  productId: string;
  offerTag: string;
  promotionalOfferEnabled: boolean;
  subscriptionGroupConfigured: boolean;
  offerHasDeadline: boolean;
  maxNotificationsPerUser: number;
  minHoursBetweenNotifications: number;
  stepDelayHours: number[];
  sendWindowLocal: string;
  testUserEmails: string[];
}

export interface WinbackStepStat {
  step: number;
  notificationVariant: string;
  sent: number;
  failed: number;
  dryRun: number;
}

export interface WinbackVariantStat {
  variant: string;
  showsDiscount: boolean;
  users: number;
  subscribed: number;
  optedOut: number;
  conversionPercent: number;
}

export interface WinbackOverview {
  campaignId: string;
  settings: WinbackSettings;
  warnings: string[];
  totalUsersInCampaign: number;
  activeUsers: number;
  completedUsers: number;
  optedOutUsers: number;
  subscribedUsers: number;
  notificationsSentTotal: number;
  notificationsFailedTotal: number;
  dryRunTotal: number;
  steps: WinbackStepStat[];
  variants: WinbackVariantStat[];
  lastNotificationSentAt: string | null;
}

/** Uygulamayı kullanan ama abone olmayan kullanıcı. */
export interface WinbackProspect {
  userEmail: string;
  userName: string;
  membershipType: string;
  installDate: string | null;
  daysSinceInstall: number;
  lastSeenDate: string | null;
  daysSinceLastSeen: number | null;
  canReceiveNotifications: boolean;
  hasAndroidDevice: boolean;
  hasIosDevice: boolean;
  availablePlatforms: string;
  timezoneOffsetMinutes: number;
  localHour: number;
  withinSendWindow: boolean;
  campaignStatus: string;
  variant: string;
  campaignNotificationsSent: number;
  optedOut: boolean;
  manualNotificationsSent: number;
  lastManualNotificationAt: string | null;
  canSendManualNow: boolean;
  blockedReason: string | null;
}

export interface WinbackProspectList {
  items: WinbackProspect[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface WinbackManualHistoryItem {
  title: string;
  body: string;
  sentAt: string;
  sendSucceeded: boolean;
  failureReason: string | null;
  sentByAdmin: string | null;
}

export interface WinbackManualSendResult {
  success: boolean;
  message: string;
  remainingToday: number;
  remainingTotal: number;
}

export interface WinbackCronResult {
  campaignId: string;
  enabled: boolean;
  testMode: boolean;
  realSendEnabled: boolean;
  candidateCount: number;
  sentCount: number;
  skippedCount: number;
  dryRunCount: number;
  errorCount: number;
  message: string;
  skipReasons: Record<string, number>;
}

export const winbackAdminService = {
  overview: async (token: string): Promise<ApiResponse<WinbackOverview>> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.WINBACK}/overview`, token, { method: 'GET' });
  },

  prospects: async (
    token: string,
    params?: {
      search?: string;
      onlyNotifiable?: boolean;
      minDaysSinceInstall?: number;
      page?: number;
      pageSize?: number;
    }
  ): Promise<ApiResponse<WinbackProspectList>> => {
    const query: Record<string, string> = {
      onlyNotifiable: String(params?.onlyNotifiable ?? true),
      minDaysSinceInstall: String(params?.minDaysSinceInstall ?? 1),
      page: String(params?.page ?? 1),
      pageSize: String(params?.pageSize ?? 50),
    };
    if (params?.search) query.search = params.search;

    return authenticatedRequest(
      `${API_CONFIG.ENDPOINTS.WINBACK}/prospects`,
      token,
      { method: 'GET' },
      query
    );
  },

  manualHistory: async (
    token: string,
    email: string
  ): Promise<ApiResponse<WinbackManualHistoryItem[]>> => {
    return authenticatedRequest(
      `${API_CONFIG.ENDPOINTS.WINBACK}/prospects/history`,
      token,
      { method: 'GET' },
      { email }
    );
  },

  /**
   * Kişiye özel bildirim gönderir.
   * Sunucu tarafındaki koruma kuralları (aktif aboneye gönderme, frekans sınırı,
   * bildirim izni) buradan AŞILAMAZ; yalnızca sessiz saat yoksayılabilir.
   */
  sendManual: async (
    token: string,
    payload: { email: string; title: string; body: string; platform: 'android' | 'ios'; ignoreSendWindow?: boolean }
  ): Promise<ApiResponse<WinbackManualSendResult>> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.WINBACK}/prospects/send`, token, {
      method: 'POST',
      body: JSON.stringify({
        email: payload.email,
        title: payload.title,
        body: payload.body,
        platform: payload.platform,
        ignoreSendWindow: payload.ignoreSendWindow ?? false,
      }),
    });
  },

  optOut: async (token: string, email: string): Promise<ApiResponse> => {
    return authenticatedRequest(
      `${API_CONFIG.ENDPOINTS.WINBACK}/prospects/opt-out`,
      token,
      { method: 'POST' },
      { email }
    );
  },

  /**
   * Otomatik kampanya turunu çalıştırır.
   * dryRun=true iken hiçbir bildirim gönderilmez (varsayılan güvenli mod).
   */
  run: async (token: string, dryRun: boolean): Promise<ApiResponse<WinbackCronResult>> => {
    return authenticatedRequest(`${API_CONFIG.ENDPOINTS.WINBACK}/run`, token, {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    });
  },
};

// Export default apiRequest for custom requests
export default apiRequest;
